import React from "react";
import { Inbox, SearchCheck, FileText, Clock3, CheckCircle2, CalendarCheck, XCircle } from "lucide-react";

// Client-facing status timeline for a personalised quote. Driven entirely by the
// existing quote.status (Sent | Viewed | Accepted | Declined | Revised) — no backend change.
// Because a quote token only exists once we've received and reviewed the property,
// the first steps are shown as already complete (accurate, not fabricated).
const STEPS = [
  { key: "received", icon: Inbox, label: "Property Received", sub: "We have your property details" },
  { key: "review", icon: SearchCheck, label: "Under Review", sub: "We reviewed scope & requirements" },
  { key: "ready", icon: FileText, label: "Quote Ready", sub: "Your personalised quote is prepared" },
  { key: "approval", icon: Clock3, label: "Awaiting Approval", sub: "Review your options and choose" },
  { key: "approved", icon: CheckCircle2, label: "Approved", sub: "You confirmed your option" },
  { key: "confirmed", icon: CalendarCheck, label: "Confirmed", sub: "We'll confirm your date" },
];

// The "current" (active/in-progress) node per status.
const ACTIVE = { Sent: 2, Revised: 2, Viewed: 3, Accepted: 4, Declined: 3 };

export const QuoteStatusTimeline = ({ status }) => {
  const declined = status === "Declined";
  const active = ACTIVE[status] ?? 2;

  const steps = declined
    ? [STEPS[0], STEPS[1], STEPS[2], { key: "declined", icon: XCircle, label: "Declined", sub: "You declined this quote" }]
    : STEPS;

  const stateFor = (i) => {
    if (declined) return i < 3 ? "done" : "declined";
    if (i < active) return "done";
    if (i === active) return "current";
    if (i === active + 1) return "next";
    return "upcoming";
  };

  return (
    <div className="mt-8" data-testid="quote-status-timeline" aria-label={`Quote status: ${status}`}>
      <ol className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-0">
        {steps.map((s, i) => {
          const st = stateFor(i);
          const gold = st === "done" || st === "current";
          const isNext = st === "next";
          const isDeclined = st === "declined";
          const ring =
            isDeclined ? "border-red-500/50 bg-red-500/15 text-red-300"
            : gold ? "border-gold/60 bg-gold/15 text-gold-soft"
            : isNext ? "border-[color:var(--border-strong)] bg-white/[0.04] text-white/80"
            : "border-[color:var(--border-subtle)] bg-black/30 text-white/40";
          const connector = (st === "done" || isDeclined) ? "bg-gold/50" : "bg-[color:var(--border-subtle)]";
          return (
            <li
              key={s.key}
              className="relative flex items-start gap-3 sm:flex-1 sm:flex-col sm:items-center sm:text-center"
              data-testid={`timeline-step-${s.key}`}
              data-state={st}
            >
              {i < steps.length - 1 && (
                <span className={`hidden sm:block absolute top-5 left-1/2 h-[2px] w-full ${connector}`} aria-hidden="true" />
              )}
              <span className={`relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border ${ring} transition-colors duration-200 ${st === "current" ? "ring-2 ring-gold/30" : ""}`}>
                <s.icon className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <div className="sm:mt-2 sm:px-1.5">
                <p className={`text-[13px] font-medium ${isDeclined ? "text-red-300" : gold || isNext ? "text-white" : "text-white/50"}`}>{s.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-c leading-snug">{s.sub}</p>
                {st === "current" && !declined && <span className="mt-1 inline-block text-[10px] uppercase tracking-[0.16em] text-gold-soft">In progress</span>}
              </div>
            </li>
          );
        })}
      </ol>
      {status === "Revised" && (
        <p className="mt-4 text-center text-xs text-muted-c">This quote was revised — you're viewing the latest version.</p>
      )}
    </div>
  );
};

export default QuoteStatusTimeline;
